import _omit from 'lodash/omit';
import {
  ContentFeedNode,
  DTConditionNode,
  DTCreativeNode,
  DTOptimizationNode,
  DTProductSelectionNode,
  DTScheduleNode,
  DTSequenceNode,
  DTStoreNode,
  DTWeightedNode,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {CLStore} from 'common/src/redux/store';
import {convertConditionAPICardToAICard} from '../schemaConverters/conditionCard/convertConditionAPICardToAICard';
import {convertContentAPICardToAICard} from '../schemaConverters/contentCard/convertContentAPICardToAICard';
import {convertCreativeAPICardToAICard} from '../schemaConverters/creativeCard/convertCreativeAPICardToAICard';
import {convertOptimizationAPICardToAICard} from '../schemaConverters/optimizationCard/convertOptimizationAPICardToAICard';
import {convertScheduleAPICardToAICard} from '../schemaConverters/scheduleCard/convertScheduleAPICardToAICard';
import {convertSequenceAPICardToAICard} from '../schemaConverters/sequenceCard/convertSequenceAPICardToAICard';
import {convertWeightedAPICardToAICard} from '../schemaConverters/weightedCard/convertWeightedAPICardToAICard';

import {sharedCampaigns} from '@cl/shared';
import {convertProductAPICardToAICard} from '../schemaConverters/productCard/convertProductAPICardToAICard';
import {convertLocationAPICardToAICard} from '../schemaConverters/locationCard/convertLocationAPICardToAICard';

/**
 * Get the prompt for the add or update card specific agnet.
 * If an existing card is provided, the prompt will be updated to include the existing card details.
 * @param config {
  prompt: the prompt to use
  cardId: an existing card id to update
  store: the store to use
}
 * @returns
 */
export default function getAddOrUpdateCardPrompt({
  prompt,
  cardId,
}: {
  prompt: string;
  cardId: number;
  store: CLStore;
}) {
  let promptToUse = prompt;

  if (!!cardId) {
    const card = sharedCampaigns.nodesRef.value[cardId];

    if (!card) {
      return `Prompt cannot be used, card with id ${cardId} not found`;
    }

    promptToUse = `Update the card with the following prompt: ${prompt}. The existing card details are: \n`;

    const cardWithoutInputs = _omit(card, ['inputs']);  

    /***
     * Convert the card to the AI card schema
     */
    switch (card.type) {
      case NodeType.WEIGHTED:
        const weightedNode = cardWithoutInputs as DTWeightedNode;
        promptToUse += `${JSON.stringify(convertWeightedAPICardToAICard(weightedNode))}`;
        break;
      case NodeType.GENERAL_OPTIMIZATION:
        const optimizationNode = cardWithoutInputs as DTOptimizationNode;
        promptToUse += `${JSON.stringify(convertOptimizationAPICardToAICard(optimizationNode))}`;
        break;
      case NodeType.CREATIVE:
        const creativeNode = cardWithoutInputs as DTCreativeNode;
        promptToUse += `${JSON.stringify(convertCreativeAPICardToAICard(creativeNode))}`;
        break;
      case NodeType.SCHEDULE:
        const scheduleNode = cardWithoutInputs as DTScheduleNode;
        promptToUse += `${JSON.stringify(convertScheduleAPICardToAICard(scheduleNode))}`;
        break;
      case NodeType.CONDITION:
        const conditionNode = cardWithoutInputs as DTConditionNode;
        promptToUse += `${JSON.stringify(convertConditionAPICardToAICard(conditionNode))}`;
        break;
      case NodeType.SEQUENCE:
        const sequenceNode = cardWithoutInputs as DTSequenceNode;
        promptToUse += `${JSON.stringify(convertSequenceAPICardToAICard(sequenceNode))}`;
        break;
      case NodeType.CONTENT_FEED:
        const contentNode = cardWithoutInputs as ContentFeedNode;
        promptToUse += `${JSON.stringify(convertContentAPICardToAICard(contentNode))}`;
        break;
      case NodeType.PRODUCTS:
        const productNode = cardWithoutInputs as DTProductSelectionNode;
        promptToUse += `${JSON.stringify(convertProductAPICardToAICard(productNode))}`;
        break;
      case NodeType.STORE:
        const storeNode = cardWithoutInputs as DTStoreNode;
        promptToUse += `${JSON.stringify(convertLocationAPICardToAICard(storeNode))}`;
        break;
    }
  }

  promptToUse += `\n IMPORTANT: Strictly distinguish ID types (e.g. Card ID vs Creative ID vs Feed ID).`;

  return promptToUse;
}
